/**
 * The ability-card registry — the single source of truth for the game's
 * non-piece cards, mirroring the piece registry in `pieces.ts`. Each ability is
 * one entry here describing its cost, how it reads, and (for relocation cards)
 * which board squares it can target. The engine, deck builder, and UI all read
 * from this table, so adding a new spell is a one-entry change plus, if it needs
 * novel targeting, a `destinations` function.
 *
 * A turn is exactly one action, so playing an ability card *is* your whole turn
 * (there is no energy/multi-action system yet). That keeps every card here a
 * single, self-contained effect.
 */
import { forwardDir, shift } from "./coords";
import { ALL_DIRECTIONS } from "./movement";
import { pieceDef } from "./pieces";
import { otherPlayer } from "./rules";
import type { AbilityId, CardTier, GameState, PieceTypeId, PlayerId, Square, Vector } from "./types";

export interface AbilityDef {
  readonly id: AbilityId;
  readonly name: string;
  /** Deck-building cost in points. */
  readonly cost: number;
  /** Unicode glyph used by the hand/deck-builder renderer. */
  readonly glyph: string;
  readonly blurb: string;
  /**
   * The targeting flow this card needs, which drives both engine validation and
   * the UI interaction:
   *   - "relocate": pick a board piece (a `from`) then an empty `to` square;
   *   - "recall": pick a board piece (a `from`) — it returns to its owner's hand;
   *   - "destroy": pick a terrain piece (a `from`) — it's removed for good;
   *   - "revive": pick a captured piece type to return to hand (no board target).
   */
  readonly target: "relocate" | "recall" | "destroy" | "revive";
  /** For relocation/recall/destroy cards: whose non-royal pieces may be picked. */
  readonly scope?: "friendly" | "any";
  /**
   * For relocation cards: the empty squares the piece on `from` may be sent to.
   * Royalty rules are handled by `scope`/source filtering, so this is pure
   * geometry over empty squares.
   */
  readonly destinations?: (state: GameState, player: PlayerId, from: Square) => Square[];
  /**
   * Optional teaching-label override (see `CardTier`). Omit to derive from cost;
   * the cheap answer-spells (Recall, Demolish) set it to "role" because they're
   * situational answers, not staples you draft in bulk.
   */
  readonly tier?: CardTier;
}

function isEmpty(state: GameState, sq: Square): boolean {
  return !state.board[sq];
}

/** Empty squares one king-step from `from`. */
function adjacentEmpty(state: GameState, from: Square): Square[] {
  const out: Square[] = [];
  for (const dir of ALL_DIRECTIONS) {
    const sq = shift(from, dir, state.config);
    if (sq !== null && isEmpty(state, sq)) out.push(sq);
  }
  return out;
}

export const ABILITIES: Readonly<Record<AbilityId, AbilityDef>> = {
  sidestep: {
    id: "sidestep",
    name: "Sidestep",
    cost: 1,
    glyph: "⤡",
    blurb: "Move any non-royal piece one square in any direction.",
    target: "relocate",
    scope: "any",
    destinations: (state, _player, from) => adjacentEmpty(state, from),
  },
  dash: {
    id: "dash",
    name: "Dash",
    cost: 1,
    glyph: "⇈",
    blurb: "Move any non-royal piece two squares straight forward.",
    target: "relocate",
    scope: "any",
    destinations: (state, _player, from) => {
      const piece = state.board[from];
      if (!piece) return [];
      const dir = forwardDir(piece.owner);
      const one = shift(from, [0, dir], state.config);
      if (one === null || !isEmpty(state, one)) return [];
      const two = shift(one, [0, dir], state.config);
      if (two === null || !isEmpty(state, two)) return [];
      return [two];
    },
  },
  recall: {
    id: "recall",
    name: "Recall",
    cost: 2,
    glyph: "↺",
    blurb: "Return any non-royal piece to its owner's hand — terrain included, sending it back to be redeployed.",
    target: "recall",
    scope: "any",
    tier: "role",
  },
  resurrection: {
    id: "resurrection",
    name: "Resurrection",
    cost: 4,
    glyph: "✝",
    blurb: "Return one of your captured pieces — even your Prince — from the graveyard to your hand, ready to redeploy.",
    target: "revive",
  },
  demolish: {
    id: "demolish",
    name: "Demolish",
    cost: 2,
    glyph: "↯",
    blurb: "Destroy a terrain piece (Mound, Hills, or Walls) anywhere on the board — yours or the enemy's. Unlike Recall it's gone for good, not returned to a hand.",
    target: "destroy",
    scope: "any",
    tier: "role",
  },
};

const ABILITY_IDS = Object.keys(ABILITIES) as AbilityId[];

export function abilityDef(id: AbilityId): AbilityDef {
  return ABILITIES[id];
}

export function isAbilityId(id: string): id is AbilityId {
  return id in ABILITIES;
}

/** Ability cards a player can buy into a deck, in display order. */
export const ABILITY_CARDS: readonly AbilityDef[] = ABILITY_IDS.map((id) => ABILITIES[id]);

// --- targeting ---------------------------------------------------------------

/**
 * The board squares whose piece a relocation or recall card may target.
 * Royalty is always immune. Relocation cards additionally skip immovable terrain
 * (the Hill) and dead-end pieces with no legal destination; recall cards instead
 * accept any redeployable piece — including the Hill, which recall is the only
 * way to clear. Revive cards have no board source.
 */
export function abilitySources(state: GameState, player: PlayerId, id: AbilityId): Square[] {
  const def = abilityDef(id);
  if (def.target !== "relocate" && def.target !== "recall" && def.target !== "destroy") return [];
  const out: Square[] = [];
  for (let sq = 0; sq < state.board.length; sq++) {
    const p = state.board[sq];
    if (!p) continue;
    const pdef = pieceDef(p.type);
    if (pdef.royal) continue; // royalty is immune to every card
    if (def.scope === "friendly" && p.owner !== player) continue;
    if (def.target === "relocate") {
      // Dash/Sidestep can shove any terrain now — a Hill, or a Valley/Ridge,
      // which move as a linked unit (see abilityDestinations). A piece with
      // nowhere to go isn't a usable target.
      if (abilityDestinations(state, player, id, sq).length === 0) continue;
    } else if (def.target === "recall") {
      // Recall returns the piece to its owner's hand, so it must be a piece
      // that can live in a hand and be redeployed.
      if (!pdef.deployable) continue;
    } else {
      // Destroy only affects terrain (the uncapturable walls) — it's the answer
      // to an enemy Hill/Valley/Ridge that Recall can't truly remove.
      if (pdef.capturable !== false) continue;
    }
    out.push(sq);
  }
  return out;
}

/** The empty squares the piece on `from` may be relocated to by ability `id`. */
export function abilityDestinations(
  state: GameState,
  player: PlayerId,
  id: AbilityId,
  from: Square,
): Square[] {
  const def = abilityDef(id);
  if (def.target !== "relocate" || !def.destinations) return [];

  const piece = state.board[from];
  if (piece?.link == null) {
    // A piece can never be sent to the square it already occupies.
    return def.destinations(state, player, from).filter((sq) => sq !== from);
  }

  // Two-cell terrain moves as a unit. Probe the ability's geometry on a board
  // with both of the piece's own cells cleared (so its partner's cell counts as
  // a legal landing square), then keep only moves where the partner's new cell
  // is also free. `to` is where the picked tile lands; the partner shifts by the
  // same vector.
  const partner = piece.link;
  const cleared = state.board.map((p, i) => (i === from || i === partner ? null : p));
  const probe: GameState = { ...state, board: cleared };
  const w = state.config.width;
  const freeForSelf = (sq: Square | null) =>
    sq !== null && (state.board[sq] == null || sq === from || sq === partner);

  return def.destinations(probe, player, from).filter((to) => {
    if (to === from) return false;
    const v: Vector = [(to % w) - (from % w), Math.floor(to / w) - Math.floor(from / w)];
    return freeForSelf(shift(partner, v, state.config));
  });
}

/**
 * The distinct piece types in `player`'s graveyard that a Resurrection can bring
 * back. A player's dead pieces live in the opponent's captured pile; deck-buildable
 * types qualify, plus the Prince (reviving it restores the King's guard). The
 * King itself can never return.
 */
export function abilityRevives(state: GameState, player: PlayerId): PieceTypeId[] {
  const graveyard = state.players[otherPlayer(player)].captured;
  const seen = new Set<PieceTypeId>();
  const out: PieceTypeId[] = [];
  for (const type of graveyard) {
    if (seen.has(type)) continue;
    // Deck-buildable pieces qualify, and so does the Prince — reviving it (then
    // redeploying) restores the King's guard. (The King itself can't return.)
    if (!pieceDef(type).deployable && type !== "prince") continue;
    seen.add(type);
    out.push(type);
  }
  return out;
}
